import { useState } from "react"
import { useDispatch } from "react-redux"
import { ToggleButtonGroup, ToggleButton } from "react-bootstrap"

import useT from "utils/hooks/useT"
import { Loading, Popup } from "components"
import { AnalysisTaskConfig } from "components/Config"
import { SampleTableForPlannedAnalysis } from "components/SampleTable"
import { analysisTasks, analyses } from "state_management"

// The analysis-task popup — the two-tab dialog ("View analysis samples" / "Edit analysis task")
// that opens on top of whatever the user is looking at.
//
// RR8 #4061: this used to live INLINE inside `ScheduleElement`, i.e. inside a plan cell, which meant
// it could only exist while the plan was mounted AND the target task happened to be rendered in that
// particular member+date cell. A "Go to analysis" from Sample View therefore had to route to the plan
// first just so a cell existed to host the dialog. Extracted here so BOTH entry points share ONE
// implementation:
//   * `ScheduleElement`      — a click on a task tile on the plan.
//   * `AnalysisTaskHost`     — a programmatic open (`visual.activeAnalysisTaskId`), from Sample View,
//                              the QA worklist, or a Reviews card — route-independent.
// One copy, deliberately: the duplicated-render-logic mistake is what let RR8 #4063 and #4068 be two
// separate bug reports for one root cause.
//
// Props:
//   taskId               — the analysis-task id (also the remount key the caller should rely on)
//   task                 — the resolved task object (may be a caller-held snapshot, see SPEC-028)
//   focusQc              — expand + scroll to "QC Results & Approvals" (SPEC-022 K2)
//   initialTab           — "TASK" (Edit analysis task) or "SAMPLE" (View analysis samples)
//   bookedAndFreeAssets  — asset availability for the task form; caller fetches it
//   loading              — the caller is still resolving what goes inside (RR8 #4061): the dialog
//                          appears IMMEDIATELY on the user's click and shows a spinner in its body
//                          until the content is ready, rather than the click doing nothing visible
//                          while an asset lookup runs
//   disabled             — render read-only
//   onClose              — the caller clears its own open-state here
export default function AnalysisTaskPopup({
  taskId,
  task,
  focusQc = false,
  initialTab = "TASK",
  bookedAndFreeAssets,
  loading = false,
  disabled = false,
  onClose,
}) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  // Which tab is showing. Seeded from the caller's intent and then owned here, so the tab is not a
  // piece of state every caller has to carry.
  const [showSamples, setShowSamples] = useState(initialTab === "SAMPLE")

  if (!taskId || (!task && !loading)) return null

  return (
    <Popup
      onCancel={onClose}
      title={
        <div className="taskTabs">
          <ToggleButtonGroup type="radio" name="taskTabs_name" value={showSamples ? "SAMPLE" : "TASK"}>
            <ToggleButton
              title={t("viewAnalysisSamples")}
              variant="primary"
              value="SAMPLE"
              id="analysis-sample-view_id"
              className="task-tab-btn"
              onChange={() => setShowSamples(true)}
            >
              {t("viewAnalysisSamples")}
            </ToggleButton>
            <ToggleButton
              title={t("editAnalysisTask")}
              variant="primary"
              value="TASK"
              id="analysis-task-view_id"
              className="task-tab-btn"
              onChange={() => setShowSamples(false)}
            >
              {t("editAnalysisTask")}
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      }
    >
      {loading && <Loading />}
      {!loading && !showSamples && (
        /* RR7 verify follow-up (#1): key on the analysis-task id so switching to ANOTHER analysis
           while this popup stays open REMOUNTS the form rather than re-rendering the same instance.
           Without it every useState initialised from the task — selected consumables (reagentLotIds),
           the reagentSeeded ref, name, dates, asset requirements, … — keeps the PREVIOUS analysis's
           values on the next one. For consumables that is a GMP data-integrity hazard (analysis B's
           result could be signed with analysis A's reagent/standard lots). The id is stable across the
           SPEC-028 snapshot refetch, so this never remounts spuriously — only on a real task switch. */
        <AnalysisTaskConfig
          key={taskId}
          task={task}
          focusQcSection={focusQc}
          cancelHandler={onClose}
          submitHandler={(res) =>
            dispatch(analysisTasks.edit(taskId, res)).then(() => {
              if (task.analysisId) dispatch(analyses.setMovable(task.analysisId))
              onClose()
            })
          }
          deleteHandler={() => dispatch(analyses.remove(task.analysisId)).then(onClose)}
          bookedAndFreeAssets={bookedAndFreeAssets}
          disabled={disabled}
        />
      )}
      {!loading && showSamples && (
        <SampleTableForPlannedAnalysis analysisId={task.analysisId} cancelHandler={onClose} readOnly={disabled} />
      )}
    </Popup>
  )
}
